/**
 * Guest session persistence for the Crucible page.
 *
 * Stores conversation state in localStorage so guests don't lose their
 * session on page refresh. The key is stable per browser ("guest") but
 * could later become per-email once the captive portal is submitted.
 *
 * Stored data:
 *   - channel messages (per agent)
 *   - Eisenboard facets
 *   - user send count + captive email status
 *   - handoff state + question counters
 *   - CrucibleEngine working doc (session_id, hypotheses, cards, etc.)
 */

import type { AgentChannelState } from '../components/GambitSidebar';
import type { Facet } from '../components/Eisenboard';
import type { WorkingDoc } from './agents/crucible';
import { sanitizeUserName } from './nameCapture';

const STORAGE_KEY = 'crucible_guest_session_v1';

/** Max age for a stored session (24 hours). */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface CrucibleSessionSnapshot {
    /** ISO timestamp of save */
    savedAt: string;
    /** Channel messages — we strip the React-only fields and keep essentials */
    channels: Array<{
        id: string;
        messages: Array<{
            id: string;
            sender: 'user' | 'agent';
            text: string;
            variant?: AgentChannelState['messages'][number]['variant'];
            fromAgent?: AgentChannelState['messages'][number]['fromAgent'];
        }>;
        isOpen: boolean;
    }>;
    /** Eisenboard facets */
    facets: Facet[];
    /** Captive portal progress */
    userSendCount: number;
    captiveEmailSubmitted: boolean;
    /** Orchestration state */
    handoffComplete: boolean;
    questionCount: number;
    maxQuestions: number;
    /** CrucibleEngine working doc (enables engine to resume context) */
    workingDoc: WorkingDoc;
    /** Conversation history for context continuity across page refreshes */
    conversationHistory?: string[];
    /** User's first name (asked by Nova on first visit) */
    userName?: string;
}

/** Save current Crucible state to localStorage. */
export function saveCrucibleSession(snapshot: CrucibleSessionSnapshot): void {
    try {
        const payload = {
            ...snapshot,
            savedAt: new Date().toISOString(),
            userName: sanitizeUserName(snapshot.userName),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // quota exceeded or private browsing — silently ignore
    }
}

/** Load a previously saved Crucible session, or null if expired / absent. */
export function loadCrucibleSession(): CrucibleSessionSnapshot | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed: CrucibleSessionSnapshot = JSON.parse(raw);

        // Expire stale sessions
        if (parsed.savedAt) {
            const age = Date.now() - new Date(parsed.savedAt).getTime();
            if (age > MAX_AGE_MS) {
                clearCrucibleSession();
                return null;
            }
        }

        // Basic shape validation
        if (!Array.isArray(parsed.channels) || !Array.isArray(parsed.facets)) {
            clearCrucibleSession();
            return null;
        }

        return {
            ...parsed,
            userName: sanitizeUserName(parsed.userName) ?? undefined,
        };
    } catch {
        clearCrucibleSession();
        return null;
    }
}

/** Remove stored session. */
export function clearCrucibleSession(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}
